/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { LegacyPortalDashboardProductType } from "@spwn-portal/grpc";
import React from "react";

import { EventSalesDetailsTableSkeleton } from "./EventSalesDetailsTableSkeleton";

import type { EventSalesDetail } from "./useGetEventSalesDetails";

const colStyles = [
  { width: "25%" },
  { width: "10%" },
  { width: "10%" },
  { width: "11%" },
  { width: "15%" },
  { width: "17%" },
  { width: "12%" },
];

type EventSalesDetailsTableProps = {
  columnLabel?: "券種" | "商品";
  error: unknown;
  isLoading: boolean;
  salesDetails: EventSalesDetail[] | undefined;
  theme?: "light" | "dark";
};

export const EventSalesDetailsTable: React.FC<EventSalesDetailsTableProps> = ({
  columnLabel = "券種",
  error,
  isLoading,
  salesDetails,
  theme = "dark",
}) => {
  const isDark = theme === "dark";
  const styles = React.useMemo(() => getStyles(isDark), [isDark]);

  if (error) {
    return (
      <EventSalesDetailsTableState
        hasError={true}
        message="売上詳細データの取得に失敗しました"
        styles={styles}
      />
    );
  }

  if (!salesDetails) {
    if (isLoading) {
      return (
        <EventSalesDetailsTableSkeleton
          columnLabel={columnLabel}
          theme={theme}
        />
      );
    }

    return (
      <EventSalesDetailsTableState
        hasError={false}
        message="データがありません"
        styles={styles}
      />
    );
  }

  if (salesDetails.length === 0) {
    return (
      <EventSalesDetailsTableState
        hasError={false}
        message="データがありません"
        styles={styles}
      />
    );
  }

  const totalRev = salesDetails.reduce(
    (acc, detail) => acc + detail.price * detail.quantity,
    0
  );

  const getProductTypeTag = (type: LegacyPortalDashboardProductType) => {
    switch (type) {
      case LegacyPortalDashboardProductType.STREAMING_TICKET:
        return { text: "配信", cssStyle: styles.tagStreaming };
      case LegacyPortalDashboardProductType.VENUE_TICKET:
        return { text: "会場", cssStyle: styles.tagVenue };
      case LegacyPortalDashboardProductType.GOODS:
        return { text: "グッズ", cssStyle: styles.tagGoods };
      case LegacyPortalDashboardProductType.DIGITAL_CONTENT:
        return { text: "デジタルコンテンツ", cssStyle: styles.tagDigital };
      case LegacyPortalDashboardProductType.TIP:
        return { text: "投げ銭", cssStyle: styles.tagTip };
      default:
        return { text: "その他", cssStyle: styles.tagOther };
    }
  };

  const rows = salesDetails.map((detail) => {
    const rev = detail.price * detail.quantity;
    const share = totalRev > 0 ? (rev / totalRev) * 100 : 0;
    const tagInfo = getProductTypeTag(detail.productType);

    return {
      name: detail.productName,
      tag: tagInfo.text,
      tagStyle: tagInfo.cssStyle,
      priceF: detail.price.toLocaleString("ja-JP"),
      soldF: detail.quantity.toLocaleString("ja-JP"),
      revF: rev.toLocaleString("ja-JP"),
      buyersF: detail.uniquePurchaseUserCount.toLocaleString("ja-JP"),
      share,
      sharePct: `${share.toFixed(1)}%`,
    };
  });

  return (
    <div css={styles.tablePanel}>
      <div css={styles.scrollContainer}>
        <table css={styles.table}>
          <colgroup>
            {colStyles.map((style, index) => (
              <col key={index} style={style} />
            ))}
          </colgroup>
          <thead css={styles.thead}>
            <tr css={styles.tableHeader}>
              <th css={[styles.headerCell, styles.headerCellLeft]}>
                {columnLabel}
              </th>
              <th css={[styles.headerCell, styles.headerCellLeft]}>販売形態</th>
              <th css={[styles.headerCell, styles.headerCellRight]}>価格</th>
              <th css={[styles.headerCell, styles.headerCellRight]}>販売数</th>
              <th css={[styles.headerCell, styles.headerCellRight]}>
                売上 (¥)
              </th>
              <th css={[styles.headerCell, styles.headerCellLeft]}>
                売上構成比
              </th>
              <th css={[styles.headerCell, styles.headerCellRight]}>
                購入者数
              </th>
            </tr>
          </thead>

          <tbody css={styles.tbody}>
            {rows.map((row, index) => (
              <tr key={`${row.name}-${index}`} css={styles.tableRow}>
                <td css={[styles.cellBase, styles.cellName]}>{row.name}</td>
                <td css={styles.cellBase}>
                  <span css={[styles.tagBase, row.tagStyle]}>{row.tag}</span>
                </td>
                <td css={[styles.cellBase, styles.cellNumber]}>{row.priceF}</td>
                <td css={[styles.cellBase, styles.cellNumber]}>{row.soldF}</td>
                <td css={[styles.cellBase, styles.cellNumberBold]}>
                  {row.revF}
                </td>
                <td css={styles.cellBase}>
                  <div css={styles.sharePctCell}>
                    <span css={styles.shareBarBg}>
                      <span css={styles.shareBarFill(row.share)} />
                    </span>
                    <span css={styles.sharePctText}>{row.sharePct}</span>
                  </div>
                </td>
                <td css={[styles.cellBase, styles.cellNumber]}>
                  {row.buyersF}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

type EventSalesDetailsTableStyles = ReturnType<typeof getStyles>;

const EventSalesDetailsTableState: React.FC<{
  hasError: boolean;
  message: string;
  styles: EventSalesDetailsTableStyles;
}> = ({ hasError, message, styles }) => (
  <div css={styles.statePanel} role={hasError ? "alert" : undefined}>
    {message}
  </div>
);

const getStyles = (isDark: boolean) => ({
  statePanel: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 24px;
    color: ${isDark ? "#b9c3cd" : "#4c5a68"};
    font-size: 14px;
    text-align: center;
    background: ${isDark ? "#1b2734" : "#ffffff"};
    border: 1px solid ${isDark ? "#2e3d4c" : "#d9e1ea"};
    border-radius: 10px;
    box-shadow: ${isDark
      ? "0 1px 2px rgba(0, 0, 0, 0.22)"
      : "0 1px 3px rgba(16, 32, 52, 0.08), 0 1px 2px rgba(16, 32, 52, 0.05)"};
  `,
  tablePanel: css`
    position: relative;
    background: ${isDark ? "#1b2734" : "#ffffff"};
    border: 1px solid ${isDark ? "#2e3d4c" : "#d9e1ea"};
    border-radius: 10px;
    box-shadow: ${isDark
      ? "0 1px 2px rgba(0, 0, 0, 0.22)"
      : "0 1px 3px rgba(16, 32, 52, 0.08), 0 1px 2px rgba(16, 32, 52, 0.05)"};
    overflow: hidden;
  `,
  scrollContainer: css`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  `,
  table: css`
    width: 100%;
    min-width: 860px;
    border-collapse: collapse;
    table-layout: fixed;
  `,
  thead: css`
    background: ${isDark ? "#233140" : "#eef2f7"};
  `,
  tbody: css`
    background: ${isDark ? "#1b2734" : "#ffffff"};
  `,
  tableHeader: css`
    /* 特別なスタイルなし */
  `,
  headerCell: css`
    padding: 11px 12px;
    color: ${isDark ? "#8b96a2" : "#66737f"};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.4px;
    vertical-align: middle;
    &:first-of-type {
      padding-left: 24px;
    }
    &:last-of-type {
      padding-right: 24px;
    }
  `,
  headerCellLeft: css`
    text-align: left;
  `,
  headerCellRight: css`
    text-align: right;
  `,
  tableRow: css`
    border-top: 1px solid ${isDark ? "#243342" : "#e7edf4"};
    transition: background-color 0.2s ease;
    &:hover {
      background: ${isDark
        ? "rgba(0, 194, 174, 0.055)"
        : "rgba(0, 137, 122, 0.05)"};
    }
  `,
  cellBase: css`
    padding: 14px 12px;
    vertical-align: middle;
    &:first-of-type {
      padding-left: 24px;
    }
    &:last-of-type {
      padding-right: 24px;
    }
  `,
  cellName: css`
    color: ${isDark ? "#ffffff" : "#16222e"};
    font-size: 14px;
    font-weight: 600;
  `,
  cellNumber: css`
    color: ${isDark ? "#ffffff" : "#16222e"};
    font-size: 14px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  `,
  cellNumberBold: css`
    color: ${isDark ? "#ffffff" : "#16222e"};
    font-size: 14px;
    text-align: right;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
  tagBase: css`
    display: inline-block;
    padding: 3px 11px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.4px;
    border-radius: 20px;
    white-space: nowrap;
  `,
  tagStreaming: css`
    color: ${isDark ? "#00c2ae" : "#00897a"};
    background: ${isDark
      ? "rgba(0, 194, 174, 0.15)"
      : "rgba(0, 137, 122, 0.12)"};
  `,
  tagVenue: css`
    color: ${isDark ? "#d6a45f" : "#a06f2a"};
    background: ${isDark
      ? "rgba(214, 164, 95, 0.16)"
      : "rgba(160, 111, 42, 0.13)"};
  `,
  tagGoods: css`
    color: ${isDark ? "#29b6f6" : "#0288d1"};
    background: ${isDark
      ? "rgba(41, 182, 246, 0.15)"
      : "rgba(2, 136, 209, 0.12)"};
  `,
  tagDigital: css`
    color: ${isDark ? "#ba68c8" : "#7b1fa2"};
    background: ${isDark
      ? "rgba(186, 104, 200, 0.15)"
      : "rgba(123, 31, 162, 0.12)"};
  `,
  tagTip: css`
    color: ${isDark ? "#f06292" : "#c2185b"};
    background: ${isDark
      ? "rgba(240, 98, 146, 0.15)"
      : "rgba(194, 24, 91, 0.12)"};
  `,
  tagOther: css`
    color: ${isDark ? "#b0b0b0" : "#616161"};
    background: ${isDark
      ? "rgba(160, 160, 160, 0.15)"
      : "rgba(97, 97, 97, 0.12)"};
  `,
  sharePctCell: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  shareBarBg: css`
    flex: 1;
    height: 7px;
    background: ${isDark ? "#243342" : "#e7edf4"};
    border-radius: 4px;
    overflow: hidden;
  `,
  shareBarFill: (width: number) => css`
    display: block;
    width: ${width}%;
    height: 100%;
    background: ${isDark ? "#00c2ae" : "#00897a"};
    border-radius: 4px;
  `,
  sharePctText: css`
    width: 48px;
    color: ${isDark ? "#b9c3cd" : "#4c5a68"};
    font-size: 12.5px;
    font-weight: 700;
    text-align: right;
    font-variant-numeric: tabular-nums;
  `,
});
